// AIDEV-NOTE: LocalStorage utilities for persisting tour completion status
// SSR-safe with guards for server-side rendering

const TOUR_STORAGE_KEY = "companion-tours";

export interface TourStorageData {
    /** Map of tour ID to completion status */
    completedTours: Record<string, boolean>;

    /** Map of tour ID to last seen timestamp */
    lastSeenTimestamps: Record<string, number>;
}

/** SSR-safe check if localStorage is available */
function isLocalStorageAvailable(): boolean {
    if (typeof window === "undefined") return false;

    try {
        const test = "__localStorage_test__";
        window.localStorage.setItem(test, test);
        window.localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
}

/** Get tour storage data from localStorage */
function getTourStorageData(): TourStorageData {
    if (!isLocalStorageAvailable()) {
        return { completedTours: {}, lastSeenTimestamps: {} };
    }

    try {
        const stored = window.localStorage.getItem(TOUR_STORAGE_KEY);
        if (!stored) {
            return { completedTours: {}, lastSeenTimestamps: {} };
        }

        const parsed = JSON.parse(stored) as TourStorageData;
        return {
            completedTours: parsed.completedTours || {},
            lastSeenTimestamps: parsed.lastSeenTimestamps || {},
        };
    } catch (error) {
        console.error("[TourStorage] Error reading tour data:", error);
        return { completedTours: {}, lastSeenTimestamps: {} };
    }
}

/** Save tour storage data to localStorage */
function saveTourStorageData(data: TourStorageData): void {
    if (!isLocalStorageAvailable()) return;

    try {
        window.localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        console.error("[TourStorage] Error saving tour data:", error);
    }
}

/** Check if a tour has been completed */
export function isTourCompleted(tourId: string): boolean {
    const data = getTourStorageData();
    return data.completedTours[tourId] === true;
}

/** Mark a tour as completed */
export function markTourCompleted(tourId: string): void {
    const data = getTourStorageData();
    data.completedTours[tourId] = true;
    data.lastSeenTimestamps[tourId] = Date.now();
    saveTourStorageData(data);
}

/** Mark a tour as skipped (same as completed, user chose not to see it) */
export function markTourSkipped(tourId: string): void {
    markTourCompleted(tourId);
}

/** Reset a specific tour (for testing/debugging) */
export function resetTour(tourId: string): void {
    const data = getTourStorageData();
    delete data.completedTours[tourId];
    delete data.lastSeenTimestamps[tourId];
    saveTourStorageData(data);
}

/** Reset all tours (for testing/debugging) */
export function resetAllTours(): void {
    if (!isLocalStorageAvailable()) return;

    try {
        window.localStorage.removeItem(TOUR_STORAGE_KEY);
    } catch (error) {
        console.error("[TourStorage] Error resetting tours:", error);
    }
}

/** Get all completed tour IDs */
export function getCompletedTours(): string[] {
    const data = getTourStorageData();
    return Object.keys(data.completedTours).filter(
        (id) => data.completedTours[id],
    );
}
